import produce from "immer"
import { STORAGE_AWS, DESC, IDLE, BLOCKS } from "const"
import {
    MtDriveFile,
    MtDriveFileCreate,
    MtDriveVersion,
    MtDriveVersionCreate,
} from "server/models"
import { InferValueTypes, OrderType } from "client/utils"
import * as types from "./types"
import * as actions from "./actions"

type ActionTypes = ReturnType<InferValueTypes<typeof actions>>

const DRIVE_LIST_STYLE = "DRIVE_LIST_STYLE"
const DRIVE_SORT_COLUMN = "DRIVE_SORT_COLUMN"
const DRIVE_SORT_ORDER = "DRIVE_SORT_ORDER"

export const initFileCreate: MtDriveFileCreate = {
    folderId: 0,
    prevFolderId: null,
    accountId: null,
    owner: null,
    name: "",
    copy: 0,
    storage: STORAGE_AWS,
    datapointReference: false,
}

export const initFile: MtDriveFile = {
    id: null,
    ...initFileCreate,
}

export const initVersionCreate: MtDriveVersionCreate = {
    storageId: null,
    storageSharedUrl: null,
    fileId: null,
    accountId: null,
    owner: null,
    name: "",
    revision: "0",
    type: "",
    size: 0,
    expiryDate: null,
    publicationDate: null,
    default: true,
    reportNumber: "",
}

export const initVersion: MtDriveVersion = {
    id: null,
    ...initVersionCreate,
}

const initDriveState: types.DriveState = {
    drivePath: null,
    baseRoute: null,
    // >>> don't change URL with these values
    outsideMode: false,
    onSelectFile: null,
    onSelectFiles: null,
    onSelectVersion: null,
    // <<<

    listStyle: window.localStorage.getItem(DRIVE_LIST_STYLE) as types.DriveListStyle || BLOCKS,

    sortColumn: window.localStorage.getItem(DRIVE_SORT_COLUMN) as types.DriveSort || "updatedAt",
    sortOrder: window.localStorage.getItem(DRIVE_SORT_ORDER) as OrderType || DESC,

    filters: {
        owner: null,
        search: null,
    },
    searchState: IDLE,

    menuShow: false,
    menuTarget: null,
    menuPlacement: "right",
    action: null,

    uploadFileList: [],

    folder: {
        folderList: [],
        emptyList: false,
        active: null,
        selected: [],

        reloadList: true,
        listLoadState: IDLE,
    },

    file: {
        fileList: [],
        emptyList: false,
        active: null,
        selected: [],

        offset: 0,
        limit: 25,
        loadMore: true,

        fileEdit: initFile,

        reloadList: true,
        reloadEdit: true,
        listLoadState: IDLE,
    },

    version: {
        versionList: {},
        versionIds: [],
        versionEdit: null,
        selected: [],

        sortColumn: "createdAt",
        sortOrder: DESC,
        filters: {
            search: null,
        },

        reloadList: true,
        reloadEdit: true,
    },
}

const driveReducer = produce((
    draft: types.DriveState = initDriveState,
    action: ActionTypes = null,
): types.DriveState => {
    switch (action.type) {
        case types.DRIVE_PATH:
            draft.drivePath = action.path
            return draft

        case types.DRIVE_OUTSIDE_MODE:
            draft.outsideMode = action.outsideMode
            return draft

        case types.DRIVE_BASE_ROUTE:
            draft.baseRoute = action.baseRoute
            return draft

        case types.DRIVE_ON_SELECT_FILE:
            draft.onSelectFile = action.onSelect
            return draft

        case types.DRIVE_ON_SELECT_FILES:
            draft.onSelectFiles = action.onSelect
            return draft

        case types.DRIVE_ON_SELECT_VERSION:
            draft.onSelectVersion = action.onSelect
            return draft

        case types.DRIVE_LIST_STYLE:
            draft.listStyle = action.listStyle
            window.localStorage.setItem(DRIVE_LIST_STYLE, action.listStyle)
            return draft

        case types.DRIVE_SORT_COLUMN:
            draft.sortColumn = action.sortColumn
            window.localStorage.setItem(DRIVE_SORT_COLUMN, action.sortColumn)
            return draft

        case types.DRIVE_SORT_ORDER:
            draft.sortOrder = action.sortOrder
            window.localStorage.setItem(DRIVE_SORT_ORDER, action.sortOrder)
            return draft

        case types.DRIVE_FILTERS:
            draft.filters = action.filters
            return draft

        case types.DRIVE_SEARCH_STATE:
            draft.searchState = action.searchState
            return draft

        case types.DRIVE_POPUP_MENU_SHOW:
            draft.menuShow = action.show
            return draft

        case types.DRIVE_POPUP_MENU:
            draft.menuTarget = action.target
            draft.menuPlacement = action.placement
            return draft

        case types.DRIVE_ACTION:
            draft.action = action.action
            return draft

        // upload file list
        case types.DRIVE_UPLOAD_FILE_LIST:
            draft.uploadFileList = action.uploadFileList
            return draft

        case types.DRIVE_UPLOAD_FILE_LIST_STATUS:
            draft.uploadFileList[action.index] = {
                ...draft.uploadFileList[action.index],
                status: action.status,
            }
            return draft

        // folders
        case types.DRIVE_FOLDER_LIST:
            draft.folder.folderList = action.folders
            return draft

        case types.DRIVE_FOLDER_LIST_EMPTY:
            draft.folder.emptyList = action.empty
            return draft

        case types.DRIVE_FOLDER_ACTIVE:
            draft.folder.active = action.active
            return draft

        case types.DRIVE_FOLDER_SELECTED:
            draft.folder.selected = action.selected
            return draft

        case types.DRIVE_FOLDER_RELOAD_LIST:
            draft.folder.reloadList = action.value
            return draft

        case types.DRIVE_FOLDER_LIST_LOAD_STATE:
            draft.folder.listLoadState = action.loadState
            return draft

        // files
        case types.DRIVE_FILE_LIST:
            draft.file.fileList = action.files
            return draft

        case types.DRIVE_FILE_LIST_EMPTY:
            draft.file.emptyList = action.empty
            return draft

        case types.DRIVE_FILE_EDIT:
            draft.file.fileEdit = action.file
            return draft

        case types.DRIVE_FILE_ACTIVE:
            draft.file.active = action.active
            return draft

        case types.DRIVE_FILE_SELECTED:
            draft.file.selected = action.selected
            return draft

        case types.DRIVE_FILE_OFFSET:
            draft.file.offset = action.offset
            return draft

        case types.DRIVE_FILE_LOAD_MORE:
            draft.file.loadMore = action.loadMore
            return draft

        case types.DRIVE_FILE_RELOAD_LIST:
            draft.file.reloadList = action.value
            return draft

        case types.DRIVE_FILE_RELOAD_EDIT:
            draft.file.reloadEdit = action.value
            return draft

        case types.DRIVE_FILE_LIST_LOAD_STATE:
            draft.file.listLoadState = action.loadState
            return draft

        // versions
        case types.DRIVE_VERSION_LIST:
            draft.version.versionList = action.versions
            return draft

        case types.DRIVE_VERSION_IDS:
            draft.version.versionIds = action.versionIds
            return draft

        case types.DRIVE_VERSION_EDIT:
            draft.version.versionEdit = action.version
            return draft

        case types.DRIVE_VERSION_SELECTED:
            draft.version.selected = action.selected
            return draft

        case types.DRIVE_VERSION_SORT:
            draft.version.sortColumn = action.sortColumn
            draft.version.sortOrder = action.sortOrder
            return draft

        case types.DRIVE_VERSION_FILTERS:
            draft.version.filters = action.filters
            return draft

        case types.DRIVE_VERSION_RELOAD_LIST:
            draft.version.reloadList = action.value
            return draft

        case types.DRIVE_VERSION_RELOAD_EDIT:
            draft.version.reloadEdit = action.value
            return draft

        default:
            return draft
    }
})
export default driveReducer
